/**
 * Iter 226 · OP Booking Widget · customer-app screen at /me/op-book
 * Partner list → doctor slots → InstaBook tier picker → confirm.
 */
import React, { useEffect, useState } from "react";
import { Stethoscope, Lightning, CheckCircle, ArrowLeft } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

async function apiGet(p) { const r = await fetch(`${API}/api${p}`); return r.json(); }
async function apiPost(p, b) {
  const r = await fetch(`${API}/api${p}`, {method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify(b)});
  const d = await r.json(); if (!r.ok) throw new Error(d.detail || r.statusText); return d;
}

const INSTABOOK_TIERS = [
  {code:"swift",    fee:99,  label:"Swift · ≤30 min"},
  {code:"priority", fee:149, label:"Priority · ≤20 min"},
  {code:"urgent",   fee:249, label:"Urgent · ≤10 min"},
];

export default function OpBookingWidget() {
  const [step, setStep] = useState("partners");   // partners → slots → confirm → done
  const [partners, setPartners] = useState([]);
  const [partner, setPartner] = useState(null);
  const [days, setDays] = useState([]);
  const [slot, setSlot] = useState(null);
  const [instabook, setInstabook] = useState(false);
  const [tier, setTier] = useState("priority");
  const [form, setForm] = useState({name:"", phone:""});
  const [booking, setBooking] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiGet("/me/op/partners").then(d => setPartners(d.partners || [])); }, []);

  const pickPartner = async (p) => {
    setPartner(p); setBusy(true); setErr("");
    try {
      const d = await apiGet(`/me/op/partners/${p.id}/slots?days_ahead=7`);
      setDays(d.days || []);
      setStep("slots");
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const book = async () => {
    if (!form.name || !form.phone) { setErr("Name and phone required"); return; }
    setBusy(true); setErr("");
    try {
      const d = await apiPost("/me/op/book", {
        slot_id: slot.id, customer_name: form.name, customer_phone: form.phone,
        is_instabook: instabook, instabook_tier: instabook ? tier : null,
      });
      setBooking(d); setStep("done");
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] p-4" data-testid="op-booking-widget">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[#B91C1C] flex items-center justify-center">
              <Stethoscope size={20} color="white" weight="fill"/>
            </div>
            <div>
              <div className="font-heading text-lg">Book an OP appointment</div>
              <div className="text-[11px] text-[#8A8780]">Step {["partners","slots","done"].indexOf(step)+1} of 3</div>
            </div>
          </div>
          {step !== "partners" && step !== "done" && (
            <button onClick={() => setStep("partners")} data-testid="op-back"
                    className="text-sm text-[#5C5A55] flex items-center gap-1">
              <ArrowLeft size={14}/> Back
            </button>
          )}
        </div>
        {err && <div className="mb-3 p-2 rounded bg-[#FEE2E2] text-[#B91C1C] text-sm">{err}</div>}

        {step === "partners" && (
          <div className="space-y-2" data-testid="op-partners-list">
            {partners.length === 0 && <div className="text-sm text-[#8A8780] p-6 bg-white rounded-lg border">No OP partners with slots yet</div>}
            {partners.map(p => (
              <button key={p.id} onClick={() => pickPartner(p)} data-testid={`op-partner-${p.id}`}
                       className="w-full text-left p-4 bg-white rounded-lg border border-[#E5E2D9] hover:border-[#B91C1C]">
                <div className="font-medium">{p.name}</div>
                <div className="text-[11px] text-[#8A8780]">{p.subcategory} · {p.service_mode}</div>
              </button>
            ))}
          </div>
        )}

        {step === "slots" && (
          <div className="space-y-3" data-testid="op-slots-list">
            {days.length === 0 && <div className="text-sm text-[#8A8780] p-6 bg-white rounded-lg border">No slots available for the next 7 days</div>}
            {days.map((d, i) => (
              <div key={i} className="bg-white rounded-lg border border-[#E5E2D9] p-4">
                <div className="text-sm font-medium mb-1">{d.doctor_name}</div>
                <div className="text-[11px] text-[#8A8780] mb-2">{d.date}</div>
                <div className="flex flex-wrap gap-1.5">
                  {d.slots.map(s => (
                    <button key={s.id} disabled={!s.available}
                             onClick={() => { setSlot(s); setStep("confirm"); }}
                             data-testid={`op-slot-${s.id}`}
                             className={`px-3 py-1.5 text-xs rounded-md ${
                               !s.available ? "bg-[#E5E2D9] text-[#8A8780] cursor-not-allowed"
                                             : "bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FCA5A5] hover:text-white"
                             }`}>
                      {s.time}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {step === "confirm" && slot && (
          <div className="bg-white rounded-lg border border-[#E5E2D9] p-5 space-y-3" data-testid="op-confirm">
            <div className="text-sm">Booking <b>{slot.doctor_name}</b> at <b>{slot.time}</b> on <b>{slot.date}</b> at <b>{partner.name}</b></div>
            <input placeholder="Full name" data-testid="op-name"
                    value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"/>
            <input placeholder="Phone" data-testid="op-phone"
                    value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"/>
            <label className="flex items-center gap-2 text-sm py-2 border-t border-b border-[#E5E2D9]">
              <input type="checkbox" data-testid="op-instabook-toggle"
                      checked={instabook} onChange={e => setInstabook(e.target.checked)}/>
              <Lightning size={16} weight="fill" color="#B45309"/>
              <span className="font-medium">Priority appointment (InstaBook)</span>
            </label>
            {instabook && (
              <div className="grid grid-cols-3 gap-2" data-testid="op-tier-grid">
                {INSTABOOK_TIERS.map(t => (
                  <button key={t.code} onClick={() => setTier(t.code)}
                           data-testid={`op-tier-${t.code}`}
                           className={`p-2 rounded-md border ${
                             tier === t.code ? "border-[#B91C1C] bg-[#FEE2E2]"
                                              : "border-[#E5E2D9]"
                           }`}>
                    <div className="text-xs font-mono">{t.code}</div>
                    <div className="font-heading text-lg">₹{t.fee}</div>
                    <div className="text-[10px] text-[#8A8780]">{t.label}</div>
                  </button>
                ))}
              </div>
            )}
            <button onClick={book} disabled={busy} data-testid="op-confirm-book"
                    className="w-full py-3 rounded-md bg-[#B91C1C] text-white font-medium disabled:opacity-50">
              {busy ? "Booking…" : `Confirm booking${instabook ? ` · ₹${INSTABOOK_TIERS.find(t=>t.code===tier)?.fee}` : ""}`}
            </button>
          </div>
        )}

        {step === "done" && booking && (
          <div className="bg-white rounded-lg border border-[#10B981] p-5 space-y-3 text-center" data-testid="op-done">
            <CheckCircle size={40} className="mx-auto text-[#10B981]" weight="fill"/>
            <div className="font-heading text-lg">Booking confirmed</div>
            <div className="font-mono text-sm">{booking.booking_no}</div>
            <div className="text-sm text-[#5C5A55]">{booking.doctor_name} · {booking.time} · {booking.date}</div>
            {booking.is_instabook && (
              <div className="p-2 rounded bg-[#FEF3C7] text-[#B45309] text-xs">
                ⚡ InstaBook {booking.instabook_tier} · ₹{booking.fee} priority fee applied
              </div>
            )}
            <div className="text-[11px] text-[#8A8780]">Please check in at the desk 5 minutes before your slot.</div>
          </div>
        )}
      </div>
    </div>
  );
}
